import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/site";
import { ScrollToTop } from "@/hooks/useLenis";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/book", label: "Book" },
    { to: "/reviews", label: "Reviews" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => setOpen(false), [pathname]);

    return (
        <>
            <ScrollToTop />
            <header
                data-testid="site-header"
                className="fixed top-0 left-0 right-0 z-50 glass shadow-[0_8px_30px_rgba(14,56,42,0.08)]"
            >
                <div className="mx-auto max-w-7xl px-5 md:px-10 h-[74px] flex items-center justify-between">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
                        <span className="h-8 w-8 rounded-full bg-forest flex items-center justify-center">
                            <span className="h-3 w-3 rounded-sm bg-sage rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                        </span>
                        <span className="font-heading font-extrabold tracking-tight text-lg text-forest leading-none">
                            NextGen<span className="text-sky">.</span>
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase()}`}
                                className={`px-4 py-2 text-sm rounded-full transition-colors duration-200 ${
                                    pathname === n.to
                                        ? "bg-forest text-sand"
                                        : "text-forest/70 hover:text-forest hover:bg-sage/40"
                                }`}
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-3">
                        <a
                            href={BUSINESS.phoneHref}
                            data-testid="header-phone"
                            className="flex items-center gap-2 text-sm font-semibold text-forest hover:text-sky transition-colors"
                        >
                            <Phone size={15} /> {BUSINESS.phone}
                        </a>
                        <Link
                            to="/book"
                            data-testid="header-cta"
                            className="rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-sand hover:bg-forestdeep hover:-translate-y-0.5 transition-[background,transform] duration-200"
                        >
                            Book a Clean
                        </Link>
                    </div>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden text-forest p-2"
                        aria-label="Menu"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>

                {open && (
                    <div className="md:hidden glass border-t border-white/40 px-5 py-6 flex flex-col gap-2">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className="py-2.5 text-forest font-medium"
                            >
                                {n.label}
                            </Link>
                        ))}
                        <a href={BUSINESS.phoneHref} className="py-2.5 text-forest font-semibold flex items-center gap-2">
                            <Phone size={16} /> {BUSINESS.phone}
                        </a>
                        <Link
                            to="/book"
                            className="mt-2 rounded-full bg-forest px-5 py-3 text-center text-sm font-semibold text-sand"
                        >
                            Book a Clean
                        </Link>
                    </div>
                )}
            </header>
        </>
    );
}
